import type { CupsLifted, CupsSetup, CupsState, CupsSwaps } from '@goodtime/shared';
import type { GameView } from './core';

export type CupsView = GameView<CupsState, CupsSwaps | CupsLifted>;

/**
 * Where each cup stands after `swaps`, as they stood before the shuffle:
 * `result[cup]` is its position. A swap exchanges whatever stands at its
 * two positions.
 */
export function cupPositions(count: number, swaps: readonly (readonly [number, number])[]): number[] {
  const at = Array.from({ length: count }, (_, i) => i);
  for (const [a, b] of swaps) {
    for (let cup = 0; cup < count; cup++) {
      if (at[cup] === a) at[cup] = b;
      else if (at[cup] === b) at[cup] = a;
    }
  }
  return at;
}

/** Where the coins end up: under the cup they started under. */
export function coinsAfter(shuffle: CupsSwaps, count: number): number {
  return cupPositions(count, shuffle.swaps)[shuffle.start] ?? shuffle.start;
}

/** How long swap `i` of `n` takes (ms): the first slow enough to follow,
 *  the last brisk, so it feels like it speeds up. */
export function swapMs(i: number, n: number): number {
  const x = n <= 1 ? 0 : i / (n - 1);
  return Math.round(640 - 260 * x);
}

/** A position in words: "left", "middle" and "right" for three cups,
 *  otherwise its number from the left. */
export function cupWords(position: number, count: number): string {
  if (count === 3) return ['left', 'middle', 'right'][position] ?? `${position + 1}`;
  return `number ${position + 1}`;
}

/** What the last act revealed, by kind. */
export const isLifted = (r: CupsSwaps | CupsLifted | null): r is CupsLifted => !!r && 'found' in r;

/** The line under the title. `shuffling` while the cups are moving. */
export function cupsLead(view: CupsView, setup: CupsSetup | undefined, shuffling: boolean): string {
  const count = setup?.cups ?? 3;
  const lifted = view.state?.lifted ?? null;
  if (view.phase === 'over' && lifted) {
    if (lifted.found) return 'You found them!';
    return `Not that one. They were under the ${cupWords(lifted.prizeAt, count)} cup.`;
  }
  if (shuffling) return 'Keep your eye on the coins…';
  if (view.state?.shuffled) return 'Where are the coins? Tap a cup.';
  return `Watch the coins go under a cup. Find them for ${(setup?.find ?? 0).toLocaleString('en-GB')} coins.`;
}

/** What the game is, said on a tap. */
export const cupsExplain = (setup: CupsSetup | undefined): string =>
  `The coins go under a cup, then the cups swap places. Follow that cup and tap it to win ${(setup?.find ?? 0).toLocaleString('en-GB')} coins.`;

/** Whether tapping a cup picks it now (shuffled, nothing lifted, still). */
export const canPick = (view: CupsView, shuffling: boolean): boolean =>
  view.phase === 'play' && !shuffling && !!view.state?.shuffled && !view.state.lifted;
